import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import BottomNav from '../components/BottomNav';
import { 
  ArrowLeft, 
  Award, 
  MessageSquare, 
  Calendar,
  Star,
  Target,
  Trophy,
  Medal,
  Gem,
  Zap,
  Loader2,
  CheckCircle,
  LogOut,
  User,
  Clock,
  Camera,
  ThumbsUp,
  ThumbsDown,
  ChevronRight,
  ChevronDown,
  X,
  Home,
  MapPin,
  FileText,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get proper photo URL
const getPhotoUrl = (photo) => {
  if (!photo) return '';
  if (photo.startsWith('http://') || photo.startsWith('https://')) {
    return photo;
  }
  const token = localStorage.getItem('token');
  return `${API_URL}/api/photo?path=${encodeURIComponent(photo)}&auth=${token}`;
};

// Badge configuration
const BADGE_CONFIG = [
  { id: 'first10', name: 'Débutant', requirement: 10, icon: '🌟', description: '10 missions terminées' },
  { id: 'first50', name: 'Confirmé', requirement: 50, icon: '🥈', description: '50 missions terminées' },
  { id: 'first100', name: 'Expert', requirement: 100, icon: '🥇', description: '100 missions terminées' },
  { id: 'first500', name: 'Légende', requirement: 500, icon: '💎', description: '500 missions terminées' },
];

const HousekeeperProfile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showCGU, setShowCGU] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openEvaluationDetail = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setShowEvaluationModal(true);
  };

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const [statsRes, evalRes, rejRes] = await Promise.all([
        fetch(`${API_URL}/api/users/${user.id}/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/users/${user.id}/evaluations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/users/${user.id}/rejections`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      let evalList = [];
      if (evalRes.ok) {
        const evalData = await evalRes.json();
        evalList = (evalData.evaluations || []).map((e) => ({
          type: 'evaluation',
          task_id: e.task_id,
          property_name: e.property_name,
          date: e.date,
          comment: e.comment,
          rating: e.rating,
          result: e.result,
          photos: e.photos || [],
          sorted_at: e.commented_at,
          author: e.commented_by,
        }));
      }

      let rejList = [];
      if (rejRes.ok) {
        const rejData = await rejRes.json();
        rejList = (rejData.rejections || []).map((r) => ({
          type: 'rejection',
          task_id: r.task_id,
          property_name: r.property_name,
          date: r.date,
          comment: r.reason,
          rating: null,
          result: 'rejected',
          photos: r.photos || [],
          sorted_at: r.rejected_at,
          author: r.rejected_by,
        }));
      }

      // Fusion + tri décroissant
      const merged = [...rejList, ...evalList].sort((a, b) => {
        const ta = a.sorted_at ? new Date(a.sorted_at).getTime() : 0;
        const tb = b.sorted_at ? new Date(b.sorted_at).getTime() : 0;
        return tb - ta;
      });
      setFeedbacks(merged);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getBadgeIcon = (icon) => {
    const icons = {
      '🏆': <Trophy className="h-6 w-6 text-amber-500" />,
      '🥈': <Medal className="h-6 w-6 text-slate-400" />,
      '🥇': <Medal className="h-6 w-6 text-amber-400" />,
      '💎': <Gem className="h-6 w-6 text-cyan-500" />,
      '🌟': <Star className="h-6 w-6 text-yellow-500" />,
      '💪': <Zap className="h-6 w-6 text-emerald-500" />
    };
    return icons[icon] || <Award className="h-6 w-6 text-slate-400" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getNextBadgeProgress = () => {
    const completed = stats?.total_completed || 0;
    for (const badge of BADGE_CONFIG) {
      if (completed < badge.requirement) {
        return {
          name: badge.name,
          current: completed,
          target: badge.requirement,
          progress: (completed / badge.requirement) * 100,
          remaining: badge.requirement - completed
        };
      }
    }
    return null;
  };

  const isUnlocked = (requirement) => {
    return (stats?.total_completed || 0) >= requirement;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1F3A8A]" />
      </div>
    );
  }

  const nextBadge = getNextBadgeProgress();

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="hover:bg-slate-100" 
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5 text-slate-700" />
          </Button>
          <h1 className="font-black text-xl text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Mon Profil
          </h1>
        </div>
      </div>

      {/* Profile Header */}
      <div className="bg-white border-b border-slate-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-slate-200 bg-slate-100 flex-shrink-0">
            {user?.avatar_url ? (
              <img 
                src={user.avatar_url} 
                alt={user.name} 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#1F3A8A]">
                <User className="h-10 w-10 text-white" />
              </div>
            )}
          </div>
          <div className="text-left">
            <h2 className="text-3xl font-black text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {user?.name}
            </h2>
            <p className="text-sm text-slate-500 mt-1">{user?.email}</p>
            {user?.siret && (
              <p className="text-xs text-slate-400 mt-1">SIRET: {user.siret}</p>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4 p-6">
        <div 
          className="bg-white p-4 rounded-xl border border-slate-200 flex flex-col items-start"
          data-testid="stats-missions"
        >
          <span className="text-2xl font-black text-[#1F3A8A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {stats?.total_completed || 0}
          </span>
          <span className="text-xs text-slate-500 mt-1">Missions</span>
        </div>
        <div 
          className="bg-white p-4 rounded-xl border border-slate-200 flex flex-col items-start"
          data-testid="stats-rating"
        >
          <span className="text-2xl font-black text-[#1F3A8A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {stats?.average_rating ? stats.average_rating.toFixed(1) : '-'}
            <span className="text-base font-normal text-slate-400">/10</span>
          </span>
          <span className="text-xs text-slate-500 mt-1">Note</span>
        </div>
        <div 
          className="bg-white p-4 rounded-xl border border-slate-200 flex flex-col items-start"
          data-testid="stats-earnings"
        >
          <span className="text-2xl font-black text-[#10B981]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {stats?.potential_earnings || 0}€
          </span>
          <span className="text-xs text-slate-500 mt-1">Gains</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6">
        <Tabs defaultValue="badges" className="w-full" data-testid="housekeeper-profile-tabs">
          <TabsList className="grid w-full grid-cols-2 bg-slate-100 p-1 rounded-xl h-12">
            <TabsTrigger 
              value="badges" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold"
              data-testid="tab-trigger-badges"
            >
              <Trophy className="h-4 w-4 mr-2" />
              Badges
            </TabsTrigger>
            <TabsTrigger 
              value="evaluations" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold"
              data-testid="tab-trigger-evaluations"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Retours
              {feedbacks.length > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                  {feedbacks.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Badges Tab */}
          <TabsContent value="badges" className="mt-6">
            {/* Progress to next badge */}
            {nextBadge && (
              <div className="bg-white p-4 rounded-xl border border-slate-200 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-slate-700">
                    Prochain badge : {nextBadge.name}
                  </span>
                  <span className="text-sm text-slate-500">
                    {nextBadge.current}/{nextBadge.target}
                  </span>
                </div>
                <Progress value={nextBadge.progress} className="h-2" />
                <p className="text-xs text-slate-500 mt-2">
                  Encore {nextBadge.remaining} mission{nextBadge.remaining > 1 ? 's' : ''} !
                </p>
              </div>
            )}

            {/* Badges Grid */}
            <div className="grid grid-cols-2 gap-4">
              {BADGE_CONFIG.map((badge) => {
                const unlocked = isUnlocked(badge.requirement);
                return (
                  <div 
                    key={badge.id}
                    className={`p-4 border rounded-xl flex flex-col items-start bg-white transition-all ${
                      unlocked 
                        ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-white' 
                        : 'border-slate-200 opacity-50 grayscale'
                    }`}
                    data-testid="badge-item"
                  >
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center mb-3 ${
                      unlocked ? 'bg-amber-100' : 'bg-slate-100'
                    }`}>
                      {getBadgeIcon(badge.icon)}
                    </div>
                    <span className="font-bold text-slate-800">{badge.name}</span>
                    <span className="text-xs text-slate-500 mt-1">{badge.description}</span>
                    {unlocked && (
                      <Badge className="mt-2 bg-amber-500 hover:bg-amber-600">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Débloqué
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Retours Tab (rejets + évaluations fusionnés) */}
          <TabsContent value="evaluations" className="mt-6">
            {feedbacks.length > 0 ? (
              <div className="flex flex-col gap-4">
                {feedbacks.map((item, idx) => {
                  const isRejection = item.type === 'rejection';
                  return (
                  <div 
                    key={`${item.type}-${item.task_id || idx}`}
                    className={`bg-white p-5 rounded-xl border flex flex-col gap-3 cursor-pointer transition-colors ${
                      isRejection ? 'border-red-200 hover:border-red-400' : 'border-slate-200 hover:border-[#1F3A8A]'
                    }`}
                    data-testid={isRejection ? 'rejection-card' : 'evaluation-card'}
                    onClick={() => openEvaluationDetail(item)}
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-bold text-[#0F172A] text-base">
                          {item.property_name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm text-slate-500">{formatDate(item.date)}</span>
                        </div>
                      </div>
                      {item.rating && (
                        <div className={`px-3 py-1.5 rounded-lg font-black text-lg ${
                          item.rating >= 8 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : item.rating >= 5 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {item.rating}/10
                        </div>
                      )}
                    </div>

                    {/* Result Badge */}
                    <div className="flex items-center gap-2">
                      {isRejection ? (
                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                          <X className="h-3 w-3 mr-1" />
                          Prestation refusée
                        </Badge>
                      ) : item.result === 'validated' ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          <ThumbsUp className="h-3 w-3 mr-1" />
                          Validé
                        </Badge>
                      ) : item.result ? (
                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                          <ThumbsDown className="h-3 w-3 mr-1" />
                          Non-conforme
                        </Badge>
                      ) : null}
                    </div>

                    {/* Motif / Commentaire */}
                    {item.comment && (
                      <div className={`rounded-lg p-3 border ${
                        isRejection ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'
                      }`}>
                        {isRejection && (
                          <p className="text-xs font-bold text-red-700 mb-1 uppercase">Motif</p>
                        )}
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {item.comment}
                        </p>
                      </div>
                    )}

                    {/* Auteur */}
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <User className="h-3.5 w-3.5" />
                      <span>
                        {isRejection ? 'Refusé par' : 'Contrôle par'} <strong>{item.author}</strong>
                      </span>
                    </div>

                    {/* Photos */}
                    {item.photos && item.photos.length > 0 && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Camera className="h-4 w-4 text-slate-400" />
                          <span className="text-xs font-semibold text-slate-600">
                            {item.photos.length} photo{item.photos.length > 1 ? 's' : ''}
                            {isRejection ? ' justificative' + (item.photos.length > 1 ? 's' : '') : ' du contrôle'}
                          </span>
                        </div>
                        <div className="flex overflow-x-auto gap-3 pb-2 snap-x scrollbar-hide">
                          {item.photos.map((photo, photoIdx) => (
                            <a
                              key={`photo-${photoIdx}`}
                              href={getPhotoUrl(photo)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="shrink-0 snap-start"
                            >
                              <img
                                src={getPhotoUrl(photo)}
                                alt={`Photo ${photoIdx + 1}`}
                                className={`h-24 w-24 rounded-lg object-cover border transition-colors ${
                                  isRejection ? 'border-red-200 hover:border-red-500' : 'border-slate-200 hover:border-[#1F3A8A]'
                                }`}
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Arrow indicator */}
                    {item.task_id && (
                      <div className="flex justify-end mt-1">
                        <ChevronRight className="h-5 w-5 text-slate-300" />
                      </div>
                    )}
                  </div>
                )})}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <CheckCircle className="h-16 w-16 mx-auto text-slate-200 mb-4" />
                <p className="text-slate-600 font-semibold">Aucun retour</p>
                <p className="text-sm text-slate-400 mt-2">
                  Les contrôles qualité et les prestations refusées<br />apparaîtront ici
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Logout Button + CGU */}
      <div className="px-6 mt-8 space-y-3">
        {/* CGU Link - Discret */}
        <button
          onClick={() => setShowCGU(true)}
          className="w-full p-3 text-center text-slate-500 text-sm hover:text-slate-700 transition-colors flex items-center justify-center gap-2"
          data-testid="cgu-button"
        >
          <FileText className="h-4 w-4" />
          {"Conditions d'utilisation"}
        </button>

        <button
          onClick={handleLogout}
          className="w-full p-4 text-center text-red-600 font-bold border-2 border-red-100 rounded-xl hover:bg-red-50 transition-colors"
          data-testid="logout-button"
        >
          <LogOut className="h-5 w-5 inline mr-2" />
          Se déconnecter
        </button>
      </div>

      <BottomNav />

      {/* CGU Modal */}
      <Dialog open={showCGU} onOpenChange={setShowCGU}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#1F3A8A]" />
              {"Conditions d'utilisation"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Checkboxes pré-cochées */}
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <Checkbox id="cgu-accept" checked={true} disabled className="mt-0.5" />
                <label htmlFor="cgu-accept" className="text-xs text-slate-600 leading-relaxed">
                  {"J'ai lu et j'accepte les Conditions Générales d'Utilisation de l'application PlayMCB."}
                </label>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <Checkbox id="cgu-geo" checked={true} disabled className="mt-0.5" />
                <label htmlFor="cgu-geo" className="text-xs text-slate-600 leading-relaxed">
                  {"J'accepte que l'application PlayMCB enregistre ma position géographique uniquement lors de la validation de mon arrivée et de mon départ d'une intervention afin de garantir la traçabilité des prestations réalisées."}
                </label>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <Checkbox id="cgu-photos" checked={true} disabled className="mt-0.5" />
                <label htmlFor="cgu-photos" className="text-xs text-slate-600 leading-relaxed">
                  {"J'accepte que les photographies, dates, horaires et informations transmises via l'application PlayMCB soient conservés comme justificatifs de réalisation des prestations."}
                </label>
              </div>
            </div>

            {/* Articles CGU */}
            <div className="pt-4 border-t border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Articles</p>
              <div className="text-[11px] text-slate-500 leading-relaxed space-y-3">
                <p>
                  {"Dans le cadre de l'utilisation de l'application PlayMCB, le prestataire indépendant reconnaît que certaines informations peuvent être collectées afin d'assurer le suivi des prestations, le contrôle qualité et la traçabilité des interventions réalisées pour le compte de Ma Conciergerie Bretonne."}
                </p>
                <p>
                  <strong>{"Lors de chaque intervention, le prestataire s'engage à :"}</strong>
                </p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>{"Valider son arrivée sur le lieu de la prestation via la fonctionnalité « Je suis arrivé » disponible dans l'application PlayMCB ;"}</li>
                  <li>{"Valider la fin de son intervention via la fonctionnalité prévue à cet effet dans l'application ;"}</li>
                  <li>{"Réaliser et transmettre les photographies demandées afin de justifier de l'état du logement et de la bonne exécution de la prestation ;"}</li>
                  <li>{"Compléter les éventuels formulaires de contrôle qualité mis à disposition dans l'application ;"}</li>
                  <li>{"Signaler toute anomalie, dégradation, panne, équipement manquant, présence de nuisibles ou toute autre situation particulière constatée dans le logement."}</li>
                </ul>
                <p>
                  {"La validation de l'arrivée et du départ peut entraîner l'enregistrement ponctuel de la position géographique du terminal utilisé. Cette géolocalisation a pour seule finalité de confirmer la présence du prestataire à l'adresse de la prestation concernée, de garantir la traçabilité de l'intervention et de faciliter le traitement des éventuelles réclamations, demandes d'indemnisation ou litiges."}
                </p>
                <p>
                  <strong>{"Aucun suivi permanent, continu ou en temps réel des déplacements du prestataire n'est effectué par l'application PlayMCB."}</strong>{" Les données de localisation sont collectées uniquement lors des actions volontaires de validation réalisées par le prestataire."}
                </p>
                <p>
                  {"Les photographies, données de localisation, dates, horaires de validation, formulaires complétés et informations enregistrées dans l'application PlayMCB constituent des éléments de preuve de la réalisation de la prestation. Ces éléments peuvent être utilisés dans le cadre du contrôle qualité, du traitement des réclamations, des demandes d'indemnisation, des procédures d'assurance, des litiges avec les voyageurs, propriétaires ou partenaires, ainsi que pour toute démarche liée à l'exécution de la prestation."}
                </p>
                <p>
                  {"Les données collectées sont conservées pendant la durée nécessaire au suivi des prestations, au contrôle qualité, au respect des obligations légales et à la défense des intérêts de Ma Conciergerie Bretonne en cas de litige."}
                </p>

                <div className="mt-4 p-3 bg-slate-100 border border-slate-300 rounded-lg space-y-2">
                  <p className="font-semibold text-slate-700 text-[11px]">
                    {"ARTICLE — FOURNITURES INCLUSES DANS LE FORFAIT ET PÉNALITÉS"}
                  </p>
                  <p>
                    <strong>{"1. Fournitures à la charge du prestataire (incluses dans le forfait ménage) :"}</strong>{" le prestataire indépendant s'engage à fournir, à ses frais et en quantité suffisante pour chaque intervention, les consommables suivants destinés au logement :"}
                  </p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li>{"papier toilette ;"}</li>
                    <li>{"éponge(s) de cuisine ;"}</li>
                    <li>{"sac(s) poubelle (cuisine et salle de bain) ;"}</li>
                    <li>{"produit vaisselle."}</li>
                  </ul>
                  <p>
                    <strong>{"2. Fournitures à la charge de Ma Conciergerie Bretonne :"}</strong>{" Ma Conciergerie Bretonne met à disposition du logement, en quantité suffisante, les produits d'accueil suivants : shampoing et gel douche."}
                  </p>
                  <p>
                    <strong>{"3. Pénalités forfaitaires en cas d'oubli ou de réclamation voyageur :"}</strong>{" en cas d'oubli, de manquement ou de réclamation justifiée d'un voyageur, d'un propriétaire ou d'un partenaire portant sur l'absence d'un consommable visé au point 1, le prestataire accepte expressément qu'une retenue forfaitaire soit appliquée sur la rémunération de la prestation concernée, selon le barème suivant :"}
                  </p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li>{"produit vaisselle non remplacé ou manquant : – 2,00 € ;"}</li>
                    <li>{"éponge non fournie ou manquante : – 1,00 € ;"}</li>
                    <li>{"sac(s) poubelle non fourni(s) ou manquant(s) : – 0,50 €."}</li>
                  </ul>
                  <p>
                    {"Ces retenues, librement consenties par le prestataire au titre de l'exécution de la prestation, peuvent se cumuler le cas échéant. Elles ne constituent ni une sanction disciplinaire ni une amende, mais une régularisation forfaitaire du prix de la prestation tenant compte du fait que le forfait initial intègre la fourniture des consommables susmentionnés."}
                  </p>
                  <p>
                    {"La constatation du manquement peut être effectuée par tout moyen : photographies transmises via l'application PlayMCB, signalement du voyageur, signalement du propriétaire, contrôle qualité réalisé par Ma Conciergerie Bretonne ou par toute autre personne mandatée."}
                  </p>
                  <p>
                    {"Le prestataire est informé du montant de la retenue applicable au moment de la validation de la prestation dans l'application, ou ultérieurement en cas de réclamation postérieure justifiée. Aucune retenue ne peut excéder le montant de la rémunération due au titre de la prestation concernée."}
                  </p>
                </div>

                <div className="mt-4 p-3 bg-slate-100 border border-slate-300 rounded-lg space-y-2">
                  <p className="font-semibold text-slate-700 text-[11px]">
                    {"ARTICLE — TARIFICATION FORFAITAIRE ET BASCULEMENT À L'HEURE"}
                  </p>
                  <p>
                    <strong>{"1. Mode de calcul du forfait :"}</strong>{" la rémunération de chaque prestation de ménage est établie sous forme d'un forfait, calculé sur la base d'un taux horaire de référence de 32,00 € de l'heure (TTC pour les prestataires non assujettis à la TVA), tenant compte du type de logement, de sa surface, du nombre de pièces, du linge éventuellement inclus et de toute spécificité communiquée au prestataire avant acceptation de la mission."}
                  </p>
                  <p>
                    <strong>{"2. Acceptation du forfait :"}</strong>{" en acceptant une mission via l'application PlayMCB, le prestataire reconnaît avoir pris connaissance du montant forfaitaire proposé et l'accepter sans réserve. Le forfait constitue alors la rémunération définitive de la prestation, indépendamment du temps réellement passé sur place, sous réserve des dispositions prévues au point 3 ci-dessous."}
                  </p>
                  <p>
                    <strong>{"3. Basculement automatique en tarification horaire en cas de dépassements répétés :"}</strong>{" lorsqu'il est constaté, à partir des données de pointage horodatées (« Je suis arrivé » / « J'ai terminé ») enregistrées dans l'application PlayMCB, que le prestataire dépasse de manière répétée le temps théorique de réalisation associé au forfait pour un même logement ou pour des logements de type équivalent, Ma Conciergerie Bretonne se réserve le droit, après en avoir informé le prestataire, de basculer la tarification de ses prestations futures vers une tarification horaire au taux de référence de 32,00 € de l'heure, au prorata du temps effectivement constaté via l'application."}
                  </p>
                  <p>
                    {"Le caractère « répété » s'entend notamment du constat d'un dépassement significatif (supérieur à 15 % du temps théorique) sur au moins trois (3) prestations effectuées dans une période glissante de soixante (60) jours."}
                  </p>
                  <p>
                    {"Le basculement en tarification horaire n'a pas d'effet rétroactif sur les prestations déjà réalisées et facturées au forfait. Il s'applique aux prestations à venir, à compter de la notification adressée au prestataire."}
                  </p>
                  <p>
                    {"Le prestataire conserve à tout moment la liberté de refuser une mission proposée. Le présent dispositif ne crée aucune obligation pour le prestataire d'accepter une mission rémunérée à l'heure."}
                  </p>
                </div>

                <p>
                  {"Le prestataire demeure un professionnel indépendant, libre dans l'organisation de son activité, de ses horaires et des moyens qu'il met en œuvre pour réaliser les prestations qu'il accepte. L'utilisation de l'application PlayMCB n'a pas pour objet ni pour effet de créer un lien de subordination entre Ma Conciergerie Bretonne et le prestataire."}
                </p>
              </div>
            </div>

            {/* Date d'acceptation */}
            <div className="pt-4 border-t border-slate-200">
              <p className="text-[10px] text-slate-400 text-center italic">
                {"Conditions d'utilisation acceptées lors de la première connexion le 02/06/2026"}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Evaluation Detail Modal */}
      <Dialog open={showEvaluationModal} onOpenChange={setShowEvaluationModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedEvaluation && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <Home className="h-5 w-5 text-[#1F3A8A]" />
                  {selectedEvaluation.property_name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Date et Note */}
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Calendar className="h-4 w-4" />
                    {formatDate(selectedEvaluation.date)}
                  </div>
                  {selectedEvaluation.rating && (
                    <div className={`px-4 py-2 rounded-lg font-black text-xl ${
                      selectedEvaluation.rating >= 8 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : selectedEvaluation.rating >= 5 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {selectedEvaluation.rating}/10
                    </div>
                  )}
                </div>

                {/* Résultat */}
                {selectedEvaluation.result && (
                  <div className="flex items-center gap-2">
                    {selectedEvaluation.type === 'rejection' ? (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-sm py-1">
                        <X className="h-4 w-4 mr-1" />
                        Prestation refusée
                      </Badge>
                    ) : selectedEvaluation.result === 'validated' ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-sm py-1">
                        <ThumbsUp className="h-4 w-4 mr-1" />
                        Contrôle validé
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-sm py-1">
                        <ThumbsDown className="h-4 w-4 mr-1" />
                        Non-conforme
                      </Badge>
                    )}
                  </div>
                )}

                {/* Commentaire / Motif */}
                {selectedEvaluation.comment && (
                  <div className={`rounded-xl p-4 border ${
                    selectedEvaluation.type === 'rejection' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                  }`}>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      {selectedEvaluation.type === 'rejection' ? 'Motif du refus' : 'Commentaire du contrôle'}
                    </p>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {selectedEvaluation.comment}
                    </p>
                  </div>
                )}

                {/* Auteur */}
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <User className="h-4 w-4" />
                  <span>
                    {selectedEvaluation.type === 'rejection' ? 'Refusé par' : 'Contrôle effectué par'}{' '}
                    <strong className="text-slate-700">
                      {selectedEvaluation.author || selectedEvaluation.commented_by || 'EQUIPE MCB'}
                    </strong>
                  </span>
                </div>

                {/* Photos */}
                {selectedEvaluation.photos && selectedEvaluation.photos.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-3 flex items-center gap-1">
                      <Camera className="h-3 w-3" />
                      {selectedEvaluation.type === 'rejection' ? 'Photos justificatives' : 'Photos du contrôle'} ({selectedEvaluation.photos.length})
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedEvaluation.photos.map((photo, photoIdx) => (
                        <a
                          key={`photo-${photoIdx}`}
                          href={getPhotoUrl(photo)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={getPhotoUrl(photo)}
                            alt={`Photo ${photoIdx + 1}`}
                            className={`w-full h-32 rounded-lg object-cover border transition-colors ${
                              selectedEvaluation.type === 'rejection'
                                ? 'border-red-200 hover:border-red-500'
                                : 'border-slate-200 hover:border-[#1F3A8A]'
                            }`}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bouton Voir la mission */}
                {selectedEvaluation.task_id && (
                  <Button 
                    variant="outline" 
                    className="w-full mt-4"
                    onClick={() => {
                      setShowEvaluationModal(false);
                      navigate(`/app/task/${selectedEvaluation.task_id}`);
                    }}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Voir la mission complète
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HousekeeperProfile;
